package com.example.mystybag.appCore.views.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.SearchTextField
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.model.UISearchCallbacks
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.views.components.WebInlineLanguageSwitcher
import dev.icerock.moko.resources.compose.painterResource

/** Same width as the main web content column (see MainView, HomePageWebView, etc.). */
private val WebClientContentColumnWidth = 1080.dp

@Composable
private fun WebHeaderChipTab(
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    val backgroundColor = if (selected) colors.onPrimaryContainer else Color.Transparent
    val textColor = if (selected) colors.surface else colors.onSurface

    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(99.dp))
            .background(backgroundColor)
            .rippleClick(action = onClick)
            .padding(horizontal = 5.dp, vertical = 5.dp),
        contentAlignment = Alignment.Center
    ) {
        androidx.compose.material3.Text(
            text = text,
            color = textColor,
            fontFamily = LocalTheme.googleSans,
            fontSize = 17.sp,
            lineHeight = 24.sp,
            fontWeight = FontWeight.Medium,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun ClientWebHeaderBar(
    selectedTab: Int?,
    homeTabIndex: Int,
    deliveryTabIndex: Int,
    favoritesTabIndex: Int,
    menuTabIndex: Int,
    searchCallbacks: UISearchCallbacks?,
    onFilterClick: () -> Unit,
    showSearchAndFilter: Boolean = true,
    showBackButton: Boolean = false,
    onBackClick: (() -> Unit)? = null,
    onHomeClick: (() -> Unit)? = null,
    onOrdersClick: (() -> Unit)? = null,
    onFavoritesClick: (() -> Unit)? = null,
    onMenuClick: (() -> Unit)? = null,
    homeLabel: String? = null,
    deliveryLabel: String? = null,
    favoritesLabel: String? = null,
    menuLabel: String? = null,
    /** Shown centered in the bar only when search, filter, and nav chip buttons are all hidden. */
    centerTitle: String? = null,
    /**
     * Optional row directly under the search + filter + nav row (same 1080.dp content width as the center column).
     * Used on web client home for active filter chips, matching mobile [HomePageView].
     */
    contentBelowSearchRow: (@Composable () -> Unit)? = null,
) {
    val colors = AppTheme.current.colors
    val hasNavTabs =
        onHomeClick != null || onOrdersClick != null || onFavoritesClick != null || onMenuClick != null
    val resolvedCenterTitle = centerTitle?.trim()?.takeIf { it.isNotEmpty() }
    val showCenterTitle =
        resolvedCenterTitle != null && !showSearchAndFilter && !hasNavTabs

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(colors.surface)
            .padding(horizontal = 24.dp, vertical = 12.dp)
    ) {
        Box(Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.align(Alignment.CenterStart),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (showBackButton && onBackClick != null) {
                Box(
                    modifier = Modifier
                        .size(48.dp)
                        .clip(CircleShape)
                        .background(colors.surfaceContainerLowest)
                        .rippleClick(action = onBackClick),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        modifier = Modifier.size(18.dp),
                        colorFilter = ColorFilter.tint(colors.primary)
                    )
                }
            }
            AsyncImage(
                link = "https://$DOMAIN/static/email_photos/logo_40.png",
                placeholder = null,
                modifier = Modifier
                    .size(36.dp)
                    .clip(RoundedCornerShape(10.dp)),
                cornerRadius = 10.dp
            )
            androidx.compose.material3.Text(
                text = "MystyBag",
                color = colors.onSurface,
                fontFamily = LocalTheme.googleSans,
                fontSize = 24.sp,
                lineHeight = 40.sp,
                fontWeight = FontWeight.Bold
            )
        }

        Row(
            modifier = Modifier
                .width(WebClientContentColumnWidth)
                .align(Alignment.Center),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (showSearchAndFilter) {
                if (searchCallbacks != null) {
                    SearchTextField(
                        modifier = Modifier.width(350.dp),
                        searchCallbacks = searchCallbacks,
                        searchHint = sharedString(SR.strings.search_products_and_places)
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .width(350.dp)
                            .height(48.dp)
                            .clip(RoundedCornerShape(24.dp))
                            .background(colors.surfaceContainerLowest)
                    )
                }
                Box(
                    modifier = Modifier
                        .size(48.dp)
                        .clip(CircleShape)
                        .background(colors.surfaceContainerLowest)
                        .rippleClick(action = onFilterClick),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.settings_icon),
                        modifier = Modifier.size(20.dp),
                        colorFilter = ColorFilter.tint(colors.primary)
                    )
                }
            }

            if (hasNavTabs) {
                Spacer(modifier = Modifier.weight(1f))
            }

            if (onHomeClick != null) {
                WebHeaderChipTab(
                    text = homeLabel ?: sharedString(SR.strings.home),
                    selected = selectedTab == homeTabIndex,
                    onClick = onHomeClick
                )
            }

            if (onOrdersClick != null) {
                WebHeaderChipTab(
                    text = deliveryLabel ?: sharedString(SR.strings.delivery),
                    selected = selectedTab == deliveryTabIndex,
                    onClick = onOrdersClick
                )
            }

            if (onFavoritesClick != null) {
                WebHeaderChipTab(
                    text = favoritesLabel ?: sharedString(SR.strings.favorites),
                    selected = selectedTab == favoritesTabIndex,
                    onClick = onFavoritesClick
                )
            }

            if (onMenuClick != null) {
                WebHeaderChipTab(
                    text = menuLabel ?: sharedString(SR.strings.menu),
                    selected = selectedTab == menuTabIndex,
                    onClick = onMenuClick
                )
            }
        }

        Box(modifier = Modifier.align(Alignment.CenterEnd)) {
            if (PlatformHelper.isWeb) {
                WebInlineLanguageSwitcher()
            }
        }

        if (showCenterTitle) {
            Texts.HeadlineSmall(
                text = resolvedCenterTitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.Center)
                    .padding(horizontal = 200.dp),
                textAlign = TextAlign.Center,
                color = colors.onSurface,
                maxLines = 1
            )
        }
        }

        if (contentBelowSearchRow != null) {
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Box(modifier = Modifier.width(WebClientContentColumnWidth)) {
                    contentBelowSearchRow()
                }
            }
        }
    }
}

@Composable
fun WebHeaderBottomDivider() {
    val colors = AppTheme.current.colors
    Box(
        Modifier
            .fillMaxWidth()
            .drawBehind {
                drawLine(
                    color = colors.outline,
                    start = Offset(0f, size.height),
                    end = Offset(size.width, size.height),
                    strokeWidth = 1f
                )
            }
    )
}
